import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Facebook, MessageCircle, Clock, ParkingCircle } from 'lucide-react';
import { LOGO_URL } from '../constants/brand';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#04140e] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* About */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="4AN" className="h-12 w-auto object-contain" />
              <div>
                <p className="font-serif font-bold text-[#c5a059]">4AN HAIR</p>
                <p className="text-[10px] text-white/60 tracking-[0.2em]">&amp; BEAUTY STUDIO</p>
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              Luxury salon located in Diegem, offering premium hair, beauty and wellness services for everyone.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.instagram.com/studio4an/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#c5a059] transition-colors" data-testid="footer-instagram" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/p/4AN-Hair-Beauty-Studio-61579470917358/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#c5a059] transition-colors" data-testid="footer-facebook" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://wa.me/32473183927?text=Hi%204AN%20Studio%2C%20I%20have%20a%20question%20about%20a%20booking" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#c5a059] transition-colors" data-testid="footer-whatsapp" aria-label="WhatsApp">
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-[#c5a059] mb-4 text-sm tracking-widest">QUICK LINKS</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-white/70 hover:text-[#c5a059] transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-white/70 hover:text-[#c5a059] transition-colors">About Us</Link></li>
              <li><Link to="/services" className="text-white/70 hover:text-[#c5a059] transition-colors">Services</Link></li>
              <li><Link to="/offers" className="text-white/70 hover:text-[#c5a059] transition-colors">Offers</Link></li>
              <li><Link to="/gallery" className="text-white/70 hover:text-[#c5a059] transition-colors">Gallery</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-[#c5a059] transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-[#c5a059] mb-4 text-sm tracking-widest">CONTACT INFO</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 text-[#c5a059] flex-shrink-0" />
                <a href="tel:+32473183927" className="hover:text-[#c5a059] transition-colors">+32 473 18 39 27</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 text-[#c5a059] flex-shrink-0" />
                <a href="mailto:info@4anhairandbeauty.be" className="hover:text-[#c5a059] transition-colors">info@4anhairandbeauty.be</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-[#c5a059] flex-shrink-0" />
                <span>Alfonse de Cockplein 5A<br/>1831 Diegem, Belgium</span>
              </li>
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="font-semibold text-[#c5a059] mb-4 text-sm tracking-widest">OPENING HOURS</h4>
            <div className="space-y-2 text-sm text-white/70">
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#c5a059]" /> 10:00 – 19:00</div>
              <p className="text-xs text-white/60">Monday Closed</p>
              <div className="flex items-center gap-2 pt-2"><ParkingCircle className="w-4 h-4 text-[#c5a059]" /> Free Parking (11 hrs)</div>
              <p className="text-xs text-white/60">We Speak: Dutch · English · Hindi</p>
            </div>
          </div>
        </div>

        <div className="border-t border-[#c5a059]/20 mt-10 pt-6">
          <div className="text-center mb-3" data-testid="footer-blessing">
            <p className="font-serif text-xs italic text-[#c5a059]/70">Shri Radhavallabh Shri Hit Harivansh</p>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <div className="text-xs text-white/60 text-center md:text-left">
              <p>© {currentYear} 4AN Hair & Beauty Studio. All Rights Reserved.</p>
              <p className="text-[10px] text-white/40 mt-1" data-testid="footer-vat">VAT: BE 1036.236.746</p>
            </div>
            <div className="flex space-x-5 text-xs">
              <Link to="/privacy" className="text-white/60 hover:text-[#c5a059] transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-white/60 hover:text-[#c5a059] transition-colors">Terms</Link>
              <Link to="/faq" className="text-white/60 hover:text-[#c5a059] transition-colors">FAQ</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
